import { useState } from "react";
import { NavLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useSidebarContext } from "@/components/ui/sidebar";
import { AnimatedLogo } from "@/components/ui/animated-logo";
import { useNotificationCounts } from "@/hooks/use-notification-counts";
import { useIsMobile } from "@/hooks/use-mobile";
import { 
  LayoutDashboard, 
  Users, 
  CreditCard, 
  BarChart2, 
  Settings, 
  Menu, 
  X,
  Share2,
  FolderKanban,
  CalendarDays,
  MessageSquare,
  ClipboardList,
  Receipt,
  Bell,
  FileText,
  Send,
  ChevronDown,
  ChevronRight,
  Zap,
  Heart,
  DollarSign,
  UserPlus,
  Target,
  BookOpen,
  Newspaper
} from "lucide-react";

interface NavItem {
  name: string;
  href: string;
  icon: any;
  countKey: string | null;
}

interface NavGroup {
  name: string;
  icon: any;
  items: NavItem[];
}

const navigation: (NavItem | NavGroup)[] = [
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard, countKey: null },
  {
    name: "Business",
    icon: Target,
    items: [
      { name: "Service Requests", href: "/dashboard/service-requests", icon: ClipboardList, countKey: "service_requests" },
      { name: "Clients", href: "/dashboard/clients", icon: Users, countKey: "clients" },
      { name: "Projects", href: "/dashboard/projects", icon: FolderKanban, countKey: "projects" },
      { name: "CRM", href: "/dashboard/crm", icon: UserPlus, countKey: null },
      { name: "Client Health", href: "/dashboard/client-health", icon: Heart, countKey: null },
    ]
  },
  {
    name: "Team",
    icon: Users,
    items: [
      { name: "Team Members", href: "/dashboard/team-members", icon: Users, countKey: null },
      { name: "Team Chat", href: "/dashboard/team-chat", icon: MessageSquare, countKey: "team_chat" },
      { name: "Team Meetings", href: "/dashboard/team-meetings", icon: CalendarDays, countKey: "team_meetings" },
    ]
  },
  {
    name: "Automation",
    icon: Zap,
    items: [
      { name: "Automation Studio", href: "/dashboard/automation-studio", icon: Zap, countKey: null },
      { name: "Communications", href: "/dashboard/communications", icon: Send, countKey: null },
      { name: "Calendar", href: "/dashboard/calendar", icon: CalendarDays, countKey: null },
    ]
  },
  {
    name: "Finance",
    icon: DollarSign,
    items: [
      { name: "Subscriptions", href: "/dashboard/subscriptions", icon: CreditCard, countKey: "subscriptions" },
      { name: "Invoices", href: "/dashboard/invoices", icon: FileText, countKey: null },
      { name: "Expenses", href: "/dashboard/expenses", icon: Receipt, countKey: null },
      { name: "Accounting", href: "/dashboard/accounting", icon: DollarSign, countKey: null },
    ]
  },
  {
    name: "Insights",
    icon: BarChart2,
    items: [
      { name: "Analytics", href: "/dashboard/analytics", icon: BarChart2, countKey: null },
      { name: "Reports", href: "/dashboard/reports", icon: Share2, countKey: null },
    ]
  },
  { name: "Notifications", href: "/dashboard/notifications", icon: Bell, countKey: null },
  { name: "Knowledge Base", href: "/dashboard/knowledge-base", icon: BookOpen, countKey: null },
  { name: "Blog Management", href: "/dashboard/blog-management", icon: Newspaper, countKey: null },
  { name: "Settings", href: "/dashboard/settings", icon: Settings, countKey: null },
];

interface SidebarHeaderProps {
  className?: string;
  children: React.ReactNode;
}

const SidebarHeader = ({ className = "", children }: SidebarHeaderProps) => (
  <div className={className}>{children}</div>
);

const SidebarContent = ({ children }: { children: React.ReactNode }) => (
  <div className="flex-1 overflow-auto">{children}</div>
);

const isNavGroup = (item: NavItem | NavGroup): item is NavGroup => {
  return 'items' in item;
};

export const DashboardSidebar = () => {
  const { isCollapsed, setIsCollapsed } = useSidebarContext();
  const { counts } = useNotificationCounts();
  const isMobile = useIsMobile();
  const [expandedGroups, setExpandedGroups] = useState<string[]>(["Business", "Team", "Automation", "Finance", "Insights"]);

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupName)
        ? prev.filter(name => name !== groupName)
        : [...prev, groupName]
    );
  };

  const renderNavItem = (item: NavItem, isInGroup: boolean = false) => {
    const count = item.countKey ? counts[item.countKey] || 0 : 0;
    
    return (
      <NavLink
        key={item.name}
        to={item.href}
        end={item.href === "/dashboard"}
        onClick={() => isMobile && setIsCollapsed(false)}
        className={({ isActive }) =>
          `flex items-center px-3 py-2.5 rounded-lg transition-all duration-200 relative touch-manipulation ${
            isActive 
              ? "bg-primary text-primary-foreground hover:bg-primary/90" 
              : "text-muted-foreground hover:bg-primary/10 hover:text-primary-light"
          } ${isCollapsed && !isMobile ? 'justify-center' : ''} ${isInGroup && !isCollapsed && !isMobile ? 'ml-4' : ''}`
        }
      >
        <div className="relative">
          <item.icon className={`h-4 w-4 ${(!isCollapsed || isMobile) && 'mr-3'}`} />
          {count > 0 && (
            <Badge 
              variant="destructive" 
              className={`absolute -top-2 ${(isCollapsed && !isMobile) ? '-right-2' : '-right-4'} h-4 min-w-4 px-1 flex items-center justify-center text-[10px] leading-none`}
            >
              {count > 99 ? '99+' : count}
            </Badge>
          )}
        </div>
        {(!isCollapsed || isMobile) && (
          <div className="flex items-center justify-between flex-1">
            <span className="text-sm font-medium">{item.name}</span>
            {count > 0 && (
              <Badge 
                variant="destructive" 
                className="h-5 min-w-5 px-1.5 flex items-center justify-center text-[10px]"
              >
                {count > 99 ? '99+' : count}
              </Badge>
            )}
          </div>
        )}
      </NavLink>
    );
  };

  const renderNavGroup = (group: NavGroup) => {
    const isExpanded = expandedGroups.includes(group.name);
    const totalCount = group.items.reduce((sum, item) => {
      return sum + (item.countKey ? counts[item.countKey] || 0 : 0);
    }, 0);

    return (
      <div key={group.name}>
        <button
          onClick={() => toggleGroup(group.name)}
          className={`w-full flex items-center px-3 py-2.5 rounded-lg transition-all duration-200 text-muted-foreground hover:bg-primary/10 hover:text-primary-light ${
            isCollapsed && !isMobile ? 'justify-center' : ''
          }`}
        >
          <div className="relative">
            <group.icon className={`h-4 w-4 ${(!isCollapsed || isMobile) && 'mr-3'}`} />
            {totalCount > 0 && (
              <Badge 
                variant="destructive" 
                className={`absolute -top-2 ${(isCollapsed && !isMobile) ? '-right-2' : '-right-4'} h-4 min-w-4 px-1 flex items-center justify-center text-[10px] leading-none`}
              >
                {totalCount > 99 ? '99+' : totalCount}
              </Badge>
            )}
          </div>
          {(!isCollapsed || isMobile) && (
            <div className="flex items-center justify-between flex-1">
              <span className="text-sm font-semibold">{group.name}</span>
              <div className="flex items-center gap-1">
                {totalCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="h-5 min-w-5 px-1.5 flex items-center justify-center text-[10px]"
                  >
                    {totalCount > 99 ? '99+' : totalCount}
                  </Badge>
                )}
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </div>
            </div>
          )}
        </button>
        {isExpanded && (!isCollapsed || isMobile) && (
          <div className="mt-1 space-y-1">
            {group.items.map(item => renderNavItem(item, true))}
          </div>
        )}
      </div>
    );
  };

  const SidebarNav = () => (
    <nav className="space-y-1 p-3">
      {navigation.map((item) => {
        if (isNavGroup(item)) {
          return renderNavGroup(item);
        }
        return renderNavItem(item);
      })}
    </nav>
  );

  // Mobile Sheet
  if (isMobile) {
    return (
      <Sheet open={isCollapsed} onOpenChange={setIsCollapsed}>
        <SheetContent side="left" className="w-[280px] p-0 bg-[#1A1F2C]">
          <SheetHeader className="border-b border-border/10 p-4">
            <SheetTitle className="flex items-center gap-2">
              <AnimatedLogo size={24} />
              <span className="font-bold text-xl tracking-tight bg-gradient-to-r from-primary via-primary-light to-primary-dark bg-clip-text text-transparent">
                Spotlight Adz
              </span>
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-auto h-[calc(100vh-80px)]">
            <SidebarNav />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop Sidebar
  return (
    <aside
      className={`fixed left-0 z-20 flex h-full flex-col transition-all duration-300 bg-[#1A1F2C] border-r border-border/10 ${
        isCollapsed ? 'w-[70px]' : 'w-[240px]'
      }`}
    >
      <SidebarHeader className="border-b border-border/10 p-4">
        <div className="flex items-center justify-between">
          {!isCollapsed ? (
            <div className="flex items-center gap-2">
              <AnimatedLogo size={24} />
              <span className="font-bold text-xl tracking-tight bg-gradient-to-r from-primary via-primary-light to-primary-dark bg-clip-text text-transparent">
                Spotlight Adz
              </span>
            </div>
          ) : (
            <AnimatedLogo size={24} />
          )}
          <Button 
            variant="ghost" 
            size="icon"
            className="hover:bg-primary/10 text-primary-light transition-colors"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? (
              <Menu className="h-5 w-5" />
            ) : (
              <X className="h-5 w-5" />
            )}
          </Button>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarNav />
      </SidebarContent>
    </aside>
  );
};